import { assertRecoverySimulationResult, encodeDirectOwnerRecovery,
  prepareRecoveryTransfer, preflightRecovery, semiModularAccountAbi, type RecoveryTransfer } from '@hoodmorning/chain';
import { robinhoodChain } from '@hoodmorning/config';
import { nonzeroAddressSchema } from '@hoodmorning/contracts';
import { encodeFunctionData, getAddress, toHex, type EIP1193Provider, type PublicClient } from 'viem';
import { z } from 'zod';
import { assertDeploymentWallet } from './deployment.js';

const uint = z.string().max(78).regex(/^(0|[1-9]\d*)$/).refine(value => BigInt(value) < 2n ** 256n);
const hash = z.templateLiteral(['0x', z.string()]).refine(value => /^0x[\da-fA-F]{64}$/.test(value))
  .transform(value => value.toLowerCase() as `0x${string}`);
const bytes = z.templateLiteral(['0x', z.string()]).refine(value => /^0x(?:[\da-fA-F]{2})+$/.test(value));
const intentSchema = z.object({ vault: nonzeroAddressSchema, recipient: nonzeroAddressSchema, amount: uint,
  asset: z.discriminatedUnion('kind', [z.object({ kind: z.literal('NATIVE') }).strict(), z.object({ kind: z.literal('ERC20'), token: nonzeroAddressSchema }).strict()]) }).strict();
const recordSchema = z.object({ version: z.literal(1), chainId: z.literal(robinhoodChain.id), attemptId: z.string().min(1).max(64),
  owner: nonzeroAddressSchema, createdAt: z.string().datetime(), intent: intentSchema,
  phase: z.enum(['reserved', 'send-invoked', 'not-broadcast']), hash: hash.nullable(),
  replacementHash: hash.nullable().optional(), replacementReason: z.enum(['repriced', 'self-replacement']).optional(),
  transaction: z.object({ to: nonzeroAddressSchema, value: z.literal('0'), data: bytes, nonce: uint }).strict() }).strict();
export type WithdrawalRecord = z.infer<typeof recordSchema>;
export class WithdrawalHashPersistenceError extends Error {
  readonly transactionHash: `0x${string}`;
  readonly acknowledgedRecord: WithdrawalRecord;
  constructor(record: WithdrawalRecord) {
    const transactionHash = hash.parse(record.hash);
    super(`Wallet acknowledged ${transactionHash}, but saving the hash failed. Preserve this hash and use read-only reconciliation; do not resend.`);
    this.name = 'WithdrawalHashPersistenceError';
    this.transactionHash = transactionHash;
    this.acknowledgedRecord = { ...structuredClone(record), hash: transactionHash };
  }
}
type Locks = Pick<LockManager, 'request'>;
const key = (vault: string) => `hoodmorning.recovery.withdrawal.v1:${robinhoodChain.id}:${getAddress(vault)}`;
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
function canonicalRecord(input: unknown): WithdrawalRecord {
  const record = recordSchema.parse(input);
  const transfer = prepareRecoveryTransfer({ vault: getAddress(record.intent.vault), recipient: getAddress(record.intent.recipient),
    asset: record.intent.asset.kind === 'NATIVE' ? { kind: 'NATIVE' } : { kind: 'ERC20', token: getAddress(record.intent.asset.token) }, amount: BigInt(record.intent.amount) });
  const data = encodeFunctionData({ abi: semiModularAccountAbi, functionName: 'execute', args: [transfer.call.to, transfer.call.value, transfer.call.data] });
  if (getAddress(record.transaction.to) !== transfer.vault || record.transaction.data.toLowerCase() !== data.toLowerCase()
    || record.phase !== 'send-invoked' && record.hash !== null
    || (record.replacementHash !== undefined && record.replacementHash !== null) !== (record.replacementReason !== undefined)
    || record.replacementHash && record.replacementHash.toLowerCase() === record.hash?.toLowerCase()) throw new Error('Noncanonical withdrawal journal');
  return record;
}
export function readWithdrawalHistory(storage: Storage, vault: string): WithdrawalRecord[] {
  const raw = storage.getItem(key(vault));
  if (raw === null) return [];
  if (raw.length > 131072) throw new Error('Withdrawal journal too large; preserve browser storage');
  const records = z.object({ version: z.literal(1), records: z.array(recordSchema).min(1).max(20) }).strict().parse(JSON.parse(raw)).records.map(canonicalRecord);
  if (new Set(records.map(record => record.attemptId)).size !== records.length
    || records.some(record => getAddress(record.intent.vault) !== getAddress(vault))) throw new Error('Withdrawal journal identity mismatch');
  return records;
}
function persist(storage: Storage, records: WithdrawalRecord[]) {
  const vault = records[0]!.intent.vault, path = key(vault), encoded = JSON.stringify({ version: 1, records });
  storage.setItem(path, encoded);
  if (storage.getItem(path) !== encoded) throw new Error('Withdrawal journal persistence failed; do not resend');
}
function update(storage: Storage, input: WithdrawalRecord, allowReplacementSupersession = false) {
  const record = canonicalRecord(input), records = readWithdrawalHistory(storage, record.intent.vault);
  const index = records.findIndex(previous => previous.attemptId === record.attemptId), previous = records[index];
  if (!previous || !same(previous.intent, record.intent) || !same(previous.transaction, record.transaction)
    || previous.owner !== record.owner || previous.createdAt !== record.createdAt
    || previous.hash !== null && previous.hash.toLowerCase() !== record.hash?.toLowerCase()
    || !allowReplacementSupersession && previous.replacementHash && previous.replacementHash.toLowerCase() !== record.replacementHash?.toLowerCase()
    || !allowReplacementSupersession && previous.replacementReason && previous.replacementReason !== record.replacementReason
    || previous.phase === 'not-broadcast' && record.phase !== 'not-broadcast'
    || previous.phase === 'send-invoked' && record.phase === 'reserved') throw new Error('Withdrawal evidence is immutable');
  records[index] = record; persist(storage, records);
}

/** A saved observation never authorizes a retry. Recheck all earlier broadcasts
 * under the same vault lock before reserving a separately reviewed transfer. */
export async function assertWithdrawalHistoryResolved(client: PublicClient, storage: Storage, vault: string) {
  for (const record of readWithdrawalHistory(storage, vault)) {
    if (record.phase === 'not-broadcast') continue;
    if (!record.hash && !record.replacementHash) throw new Error('An earlier withdrawal outcome is unknown. Reconcile it before another send.');
    const observed = await observeWithdrawal(client, record);
    if (observed.status === 'self-replacement-observed') throw new Error('Successful same-nonce self-transaction effects remain unverified; do not send another withdrawal');
  }
}

export async function submitWithdrawal(input: {
  transfer: RecoveryTransfer; owner: string; provider: EIP1193Provider; client: PublicClient;
  storage: Storage; locks: Locks; assertCurrent: () => void; previousAttemptId: string | null;
}): Promise<WithdrawalRecord> {
  const { provider, client, storage, locks, assertCurrent } = input;
  const owner = getAddress(input.owner), transfer = structuredClone(input.transfer), previousAttemptId = input.previousAttemptId;
  // Validate the displayed call as well as the intent, before any asynchronous work.
  const intent = intentSchema.parse({ vault: transfer.vault, recipient: transfer.recipient, asset: transfer.asset, amount: String(transfer.amount) });
  assertCurrent();
  return locks.request(key(transfer.vault), async () => {
    if ((readWithdrawalHistory(storage, transfer.vault).at(-1)?.attemptId ?? null) !== previousAttemptId) throw new Error('Withdrawal history changed; explicitly prepare and review again');
    assertCurrent(); await assertWithdrawalHistoryResolved(client, storage, transfer.vault); assertCurrent();
    await assertDeploymentWallet(provider, owner); assertCurrent();
    const preflight = await preflightRecovery(client, transfer.vault, owner); assertCurrent();
    const transaction = encodeDirectOwnerRecovery(preflight, transfer);
    const nonce = await client.getTransactionCount({ address: owner, blockTag: 'latest' });
    if (!Number.isSafeInteger(nonce) || nonce < 0) throw new Error('Unsupported owner transaction nonce');
    if (nonce !== await client.getTransactionCount({ address: owner, blockTag: 'pending' })) throw new Error('Owner has pending transactions; reconcile them before recovery');
    const simulation = await client.call({ account: owner, ...transaction });
    assertRecoverySimulationResult(transfer, simulation.data); assertCurrent();
    const records = readWithdrawalHistory(storage, transfer.vault);
    if (records.length >= 20) throw new Error('Withdrawal history full; no evidence was evicted');
    const record: WithdrawalRecord = { version: 1, chainId: robinhoodChain.id, attemptId: crypto.randomUUID(), owner, intent,
      createdAt: new Date().toISOString(), phase: 'reserved', hash: null,
      transaction: { ...transaction, value: '0', nonce: String(nonce) } };
    canonicalRecord(record); records.push(record); persist(storage, records);
    let invoked = false;
    try {
      await assertDeploymentWallet(provider, owner); assertCurrent();
      if (await client.getChainId() !== robinhoodChain.id || nonce !== await client.getTransactionCount({ address: owner, blockTag: 'pending' })
        || nonce !== await client.getTransactionCount({ address: owner, blockTag: 'latest' })) throw new Error('Recovery chain or owner nonce changed');
      assertCurrent(); record.phase = 'send-invoked'; update(storage, record);
      // No await between this final guard and the one wallet invocation. A lost
      // acknowledgement, rejection or crash never proves no broadcast occurred.
      assertCurrent(); invoked = true;
      const result = await provider.request({ method: 'eth_sendTransaction', params: [{ from: owner, to: transaction.to,
        value: '0x0', data: transaction.data, nonce: toHex(nonce), chainId: toHex(robinhoodChain.id) }] });
      record.hash = hash.parse(result);
      // Preserve an acknowledged hash even if the wallet/form changed meanwhile.
      try { update(storage, record); }
      catch { throw new WithdrawalHashPersistenceError(record); }
      return structuredClone(record);
    } catch (error) {
      if (!invoked) { record.phase = 'not-broadcast'; update(storage, record); }
      throw error;
    }
  });
}

function transactionKind(record: WithdrawalRecord, tx: Awaited<ReturnType<PublicClient['getTransaction']>>) {
  if (getAddress(tx.from) !== getAddress(record.owner) || BigInt(tx.nonce) !== BigInt(record.transaction.nonce))
    throw new Error('Withdrawal transaction does not match the saved owner and nonce');
  const intended = Boolean(tx.to) && getAddress(tx.to!) === getAddress(record.transaction.to)
    && tx.input.toLowerCase() === record.transaction.data.toLowerCase() && tx.value === 0n;
  if (intended) return record.replacementHash ? 'repriced' as const : 'original' as const;
  // Match the installed viem cancellation heuristic only to identify a
  // same-nonce owner self-transaction. Its contract/delegated effects are not
  // proven absent, so it remains fenced from authorizing another withdrawal.
  if (record.replacementHash && tx.to && getAddress(tx.from) === getAddress(tx.to) && tx.value === 0n && tx.input === '0x') return 'self-replacement' as const;
  throw new Error('Replacement transaction is neither the reviewed withdrawal nor a bounded owner self-replacement');
}
function assertTransaction(record: WithdrawalRecord, tx: Awaited<ReturnType<PublicClient['getTransaction']>>) {
  const selectedHash = record.replacementHash ?? record.hash;
  if (!selectedHash || tx.hash.toLowerCase() !== selectedHash.toLowerCase()) throw new Error('Withdrawal transaction hash does not match saved evidence');
  const kind = transactionKind(record, tx);
  if (record.replacementReason && record.replacementReason !== kind) throw new Error('Withdrawal replacement reason does not match the transaction');
  return kind;
}
/** User-selected hash import performs reads only and never clears the fence. */
export async function importWithdrawalHash(input: {
  client: PublicClient; storage: Storage; locks: Locks; vault: string; transactionHash: string; assertCurrent: () => void;
}) {
  const { client, storage, locks, assertCurrent, vault } = input, selectedHash = hash.parse(input.transactionHash);
  return locks.request(key(vault), async () => {
    let record = readWithdrawalHistory(storage, vault).at(-1);
    if (!record || record.phase === 'not-broadcast') throw new Error('No matching unresolved withdrawal attempt');
    const previouslySelected = record.replacementHash ?? record.hash;
    let supersedesReplacement = false, requiresCanonicalAdmission = false;
    assertCurrent(); if (await client.getChainId() !== robinhoodChain.id) throw new Error('Recovery RPC chain mismatch');
    const transaction = await client.getTransaction({ hash: selectedHash });
    if (previouslySelected?.toLowerCase() === selectedHash.toLowerCase()) {
      assertTransaction(record, transaction);
    } else if (record.hash?.toLowerCase() === selectedHash.toLowerCase()) {
      // The original transaction may become canonical after a pending or
      // reorged replacement disappears. Select it only after canonical proof.
      record = { ...record, replacementHash: undefined, replacementReason: undefined };
      assertTransaction(record, transaction);
      supersedesReplacement = true; requiresCanonicalAdmission = true;
    } else if (!record.hash) {
      // The wallet may have lost the original acknowledgement before the owner
      // replaced that nonce. An exact reviewed call can become the primary
      // evidence; a bounded self-send is retained explicitly as nonce evidence
      // even though its execution effects and the original hash remain unknown.
      const candidate = { ...record, replacementHash: selectedHash };
      const kind = transactionKind(candidate, transaction);
      if (kind === 'self-replacement') {
        record = { ...record, replacementHash: selectedHash, replacementReason: 'self-replacement' };
        requiresCanonicalAdmission = true;
      } else {
        record = { ...record, hash: selectedHash, replacementHash: undefined, replacementReason: undefined };
      }
      assertTransaction(record, transaction);
      supersedesReplacement = Boolean(previouslySelected);
      requiresCanonicalAdmission ||= Boolean(previouslySelected);
    } else {
      const candidate = { ...record, replacementHash: selectedHash };
      const reason = transactionKind(candidate, transaction);
      const next: WithdrawalRecord = { ...candidate, replacementReason: reason === 'self-replacement' ? 'self-replacement' : 'repriced' };
      assertTransaction(next, transaction);
      supersedesReplacement = Boolean(record.replacementHash); requiresCanonicalAdmission = true; record = next;
    }
    // A different same-nonce candidate, and every owner self-replacement, becomes durable
    // only after it proves exact canonical inclusion. This covers pending
    // replacements, original-wins races and later reorgs without a resend.
    if (requiresCanonicalAdmission) { await observeWithdrawal(client, record); assertCurrent(); }
    if (await client.getChainId() !== robinhoodChain.id) throw new Error('Recovery RPC chain changed');
    assertCurrent(); record.phase = 'send-invoked'; update(storage, record, supersedesReplacement);
    return readWithdrawalHistory(storage, vault).at(-1)!;
  });
}

/** Selected receipt + actual transaction + canonical inclusion. Arbitrary ERC-20
 * recipient delivery is always unverified, including with matching logs/deltas.
 * This is an observation from the selected RPC, not a finality guarantee. */
export async function observeWithdrawal(client: PublicClient, input: WithdrawalRecord) {
  const record = canonicalRecord(structuredClone(input));
  const selectedHash = record.replacementHash ?? record.hash;
  if (!selectedHash || record.phase !== 'send-invoked') throw new Error('Withdrawal outcome unknown; obtain the transaction hash from the owner wallet');
  if (await client.getChainId() !== robinhoodChain.id) throw new Error('Recovery RPC chain mismatch');
  const receipt = await client.getTransactionReceipt({ hash: selectedHash });
  const tx = await client.getTransaction({ hash: selectedHash }); const kind = assertTransaction(record, tx);
  const expectedReceiptTarget = kind === 'self-replacement' ? record.owner : record.intent.vault;
  if (receipt.transactionHash.toLowerCase() !== selectedHash.toLowerCase() || tx.blockHash?.toLowerCase() !== receipt.blockHash.toLowerCase() || tx.blockNumber !== receipt.blockNumber
    || tx.transactionIndex !== receipt.transactionIndex || getAddress(receipt.from) !== getAddress(record.owner)
    || !receipt.to || getAddress(receipt.to) !== getAddress(expectedReceiptTarget) || !Number.isSafeInteger(receipt.transactionIndex)
    || receipt.transactionIndex < 0) throw new Error('Withdrawal receipt identity mismatch');
  const block = await client.getBlock({ blockNumber: receipt.blockNumber });
  function assertBlock(candidate: typeof block) {
    if (candidate.number !== receipt.blockNumber || candidate.hash?.toLowerCase() !== receipt.blockHash.toLowerCase()
      || candidate.transactions[receipt.transactionIndex]?.toLowerCase() !== selectedHash) throw new Error('Withdrawal receipt reorg or missing inclusion');
  }
  assertBlock(block);
  // Token-controlled logs and balanceOf values cannot prove transaction-specific
  // delivery. Canonical exact inclusion resolves this send, not its token effect.
  assertBlock(await client.getBlock({ blockNumber: receipt.blockNumber }));
  if (await client.getChainId() !== robinhoodChain.id) throw new Error('Recovery RPC chain changed');
  if (receipt.status !== 'success' && receipt.status !== 'reverted') throw new Error('Unknown withdrawal receipt status');
  return { status: receipt.status === 'success'
    ? kind === 'self-replacement' ? 'self-replacement-observed' as const
    : record.intent.asset.kind === 'ERC20' ? 'token-transaction-observed' as const : 'transfer-observed' as const
    : kind === 'self-replacement' ? 'self-replacement-reverted' as const : 'reverted' as const, receipt };
}
